import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { Routes, Route } from "react-router";
import { App, RequirePolicy, createEmbed } from "@livefree/applications";
import { ReactiveCore, replaceRows, DexieDatabaseProvider, useSuccessReactor } from "@livefree/reactive";
import { MqlRequestBuilder, RequestMetadata, dexieRequestBrokerAddon, Sorting, RequestBrokerDataGrid, RequestBroker, Paging } from "@livefree/request-broker";
import { EnvironmentConfigurations, useComponent, useRuntimeProvider } from "@livefree/runtime";
import { useEffect, useCallback, useMemo, useState, createElement } from "react";
import { Pill, withDrawerController, CopyToClipboard, useDrawerController, Forbidden, WidgetField, formatPhone } from "@livefree/react-ui";
import { FilterCustom, Filters, FilterConstant, FilterSelect, FilterDateRange, FilterInput, useFiltersProvider, HorizontalFilters, HorizontalChips } from "@livefree/filters";
import { parseAndFormatDate, parseAndFormatTime, parseAndFormatDateTime } from "@livefree/common";
import { EmptyState, Widget } from "@heroui-pro/react";
import { Breadcrumbs, BreadcrumbsItem, Skeleton } from "@heroui/react";
const DevicesConfig = EnvironmentConfigurations.hydrate({
  development: {
    host: "https://localhost:9002",
    api: {
      host: "https://localhost:7104",
      apiKey: "DevicesAPI"
    }
  },
  staging: {
    host: "https://lf-artemis-staging.azurewebsites.net",
    api: {
      host: "https://lf-artemis-api-staging.azurewebsites.net",
      apiKey: "DevicesAPI"
    }
  },
  production: {
    host: [
      "https://artemis.lifebeacon.com",
      "https://app.alert1.com",
      "https://app.lifebeacon.com"
    ],
    api: {
      host: "https://artemis.lfapi.net",
      apiKey: "DevicesAPI"
    }
  }
});
const reactive = new ReactiveCore("devices");
const DevicesSDK = window.ArtemisSDK;
class DeviceActivityClient extends DevicesSDK.DeviceTrafficClient {
  /**
   * The request-broker metadata compiled to MQL, or the default first page. `withCount` asks for
   * the total; a caller paging a list too large to count (all traffic) turns it off and pages
   * with the simple pager.
   */
  queryDeviceActivity(metadata, actionKey, { withCount = true } = {}) {
    const query = metadata ? MqlRequestBuilder.create(metadata, { withCount }).build("DeviceTrafficModel") : `FETCH DeviceTrafficModel SORTBY LoggedOnUtc DESC TAKE 25${withCount ? " WITH COUNT" : ""};`;
    return super.queryDeviceTraffic(query, actionKey);
  }
}
const DeviceActivityService = reactive.service("deviceActivity", DeviceActivityClient, {
  recursive: true,
  recurseDepth: 2
})((Service) => new Service(DevicesConfig));
DeviceActivityService.queryDeviceActivity.latest(([, actionKey]) => actionKey);
DeviceActivityService.reactor("deviceActivityReactor", {
  success: (response, services, db) => {
    const { source, args, payload } = response;
    switch (source) {
      case DeviceActivityService.queryDeviceActivity: {
        const [, actionKey] = args;
        RequestMetadata.setCount(actionKey ?? source.action, payload.totalCount);
        return replaceRows(db, db.device_activity, payload.values);
      }
      default:
        return;
    }
  },
  error: (response) => {
    appRuntime.services.alerts.error(response.payload?.message ?? "Error");
  }
});
class DevicesClient extends DevicesSDK.DevicesClient {
  /**
   * `GET /devices?customerId=` — a customer's devices (an empty list when they have none). The
   * SDK method is generated from the API's `GetDevicesByCustomer` route, so the API must be running
   * a build that has it.
   */
  findByCustomer(customerId) {
    return super.getDevicesByCustomer(Number(customerId));
  }
}
const DevicesService = reactive.service("devices", DevicesClient, {
  recursive: true,
  recurseDepth: 2
})((Service) => new Service(DevicesConfig));
DevicesService.reactor("devicesReactor", {
  error: (response) => appRuntime.services.alerts.error(response.payload?.message ?? "Error")
});
const storeFormat = (store) => Object.entries(store).reduce((result, [key, value]) => {
  result[key] = value.join(",");
  return result;
}, {});
const ReactiveConfig = {
  db: {
    name: "livefree.devices.db",
    provider: new DexieDatabaseProvider([dexieRequestBrokerAddon]),
    init: (db) => {
      db.version(1).stores(storeFormat(_store));
    }
  }
};
const _store = {
  device_activity: ["&id", "customerId"]
};
const render = reactive.configure(ReactiveConfig).render;
const Shell = ({ children }) => /* @__PURE__ */ jsx("div", { className: "p-4 h-full flex flex-col", children });
let Bootstrapped;
const AppLayout = render(({ children }) => {
  Bootstrapped ??= App.bootstrap(Shell, { runtime: appRuntime });
  return /* @__PURE__ */ jsx(Bootstrapped, { children });
});
const RegisteredComboBox = ({ registryName, ...props }) => {
  const ComboBox = useComponent(registryName);
  return /* @__PURE__ */ jsx(ComboBox, { ...props });
};
class RegisteredSearchFilter extends FilterCustom {
  /**
   * @param {'custom'|'global'} [options.scope='custom'] `custom` renders inline in the toolbar
   *   without the filter chrome; `global` puts it in the filter drawer like any other filter.
   * @param {object} [options.comboBoxProps] Extra props for this combo box only, since the customer
   *   and dealer boxes do not share every prop.
   */
  constructor(name, title, registryName, { scope = "custom", comboBoxProps = {} } = {}) {
    super({
      name,
      title,
      customRender: ({ filter, value, setValue }) => {
        useEffect(() => {
          if ((value === void 0 || value === null) && filter.isApplied) {
            filter.setApplied(false);
          }
        }, [filter, value]);
        return /* @__PURE__ */ jsx(
          RegisteredComboBox,
          {
            ...comboBoxProps,
            registryName,
            name: filter.name,
            label: scope === "custom" ? void 0 : filter.title,
            "aria-label": filter.title,
            isClearable: true,
            defaultValue: value,
            onSelect: (accountId, result) => {
              if (accountId === null) {
                filter.displayValue = "";
                setValue(null, true, true);
                filter.setApplied(false);
                return;
              }
              filter.displayValue = result?.name ?? String(accountId);
              filter.setApplied(true);
              setValue(accountId, true, true);
            }
          }
        );
      },
      options: { scope, searchType: "equals" },
      // Inline, the combo box is its own chrome; in the drawer it gets the standard container.
      // An account is only ever matched by id, so the search-type picker stays hidden.
      render: { hideContainer: scope === "custom", hideSearchType: true }
    });
  }
  formattedValue() {
    if (this.value === void 0 || this.value === null) return void 0;
    return Number(this.value);
  }
}
const TYPE_OPTIONS = [
  { value: "Voice", label: "Voice" },
  { value: "SMS", label: "SMS" },
  { value: "HTTP", label: "HTTP" }
];
const DIRECTION_OPTIONS = [
  { value: "Send", label: "Send" },
  { value: "Receive", label: "Receive" }
];
const text = (name, title, options = {}) => new FilterInput({
  name,
  title,
  options: { searchType: "contains", isClearable: true, ...options },
  // Inline (custom scope) it is a bare input that applies on blur; in the drawer it gets the
  // standard container with its reset and apply.
  render: { hideSearchType: true, hideContainer: options.scope === "custom" }
});
const inline = { scope: "custom" };
const createDeviceActivityFilters = ({ customerId, deviceId } = {}) => new Filters([
  customerId != null ? new FilterConstant({ name: "customerId", title: "Customer", defaultValue: customerId }) : new RegisteredSearchFilter("customerId", "Customer", "accounts.customer-search-combo-box"),
  deviceId != null ? new FilterConstant({ name: "deviceId", title: "Device Serial", defaultValue: deviceId }) : text("deviceId", "Device Serial", { ...inline, searchType: "equals" }),
  new RegisteredSearchFilter("dealerId", "Dealer", "accounts.dealer-search-combo-box", {
    scope: "global",
    // An empty dealer filter means "every dealer", so it must not narrow itself to the signed-in
    // user's dealer on load.
    comboBoxProps: { defaultToCurrentDealer: false }
  }),
  new FilterSelect({
    name: "type",
    title: "Type",
    loader: () => Promise.resolve(TYPE_OPTIONS)
  }),
  new FilterSelect({
    name: "direction",
    title: "Direction",
    loader: () => Promise.resolve(DIRECTION_OPTIONS)
  }),
  text("phoneNumber", "Phone Number", { ...inline, searchType: "equals" }),
  text("rawValue", "Raw Value"),
  text("functionName", "Function Name"),
  new FilterDateRange({ name: "loggedOnUtc", title: "Logged On" })
]);
const createDeviceActivitySorting = () => new Sorting(
  [
    { value: "loggedOnUtc", title: "Logged On" },
    { value: "type", title: "Type" },
    { value: "direction", title: "Direction" },
    { value: "deviceId", title: "Device Serial" },
    { value: "phoneNumber", title: "Phone Number" },
    { value: "fromServerName", title: "Server Name" }
  ],
  "loggedOnUtc",
  "desc"
);
const dash$1 = /* @__PURE__ */ jsx("span", { className: "text-[var(--fg-3)]", children: "—" });
const DATE_FORMAT = { month: "2-digit", day: "2-digit", year: "numeric" };
const TIME_FORMAT = { hour: "numeric", minute: "2-digit", timeZoneName: "short" };
const TYPE_TONES = { Voice: "info", SMS: "neutral", HTTP: "warn" };
const ACCOUNT_LINK = "accounts.account-link";
const stopRowPress = (event) => event.stopPropagation();
const buildColumns = ({ showCustomer, showDevice }) => [
  { propertyName: "loggedOnUtc", name: "Date", isSortable: true, width: 50 },
  ...showCustomer ? [{ propertyName: "customerId", name: "Customer", isSortable: true }] : [],
  { propertyName: "type", name: "Type", isSortable: true },
  { propertyName: "direction", name: "Direction", isSortable: true },
  ...showDevice ? [{ propertyName: "deviceId", name: "Device Number", isSortable: true }] : [],
  { propertyName: "phoneNumber", name: "Phone Number", isSortable: true },
  // Takes the width the other columns leave over, so a long value is clipped (see `renderCell`)
  // instead of widening the grid.
  { propertyName: "rawValue", name: "Raw Value", isSortable: false, width: "3fr", minWidth: 200 },
  { propertyName: "fromServerName", name: "Server Name", isSortable: true }
];
const DeviceActivityTable = ({
  rows = [],
  showCustomer = false,
  showDevice = true,
  onSelectRow,
  topContent,
  pagerStyle
}) => {
  const { components } = useRuntimeProvider();
  const AccountLink = components.get(ACCOUNT_LINK);
  const renderCell = useCallback(
    (row, columnKey) => {
      switch (columnKey) {
        case "loggedOnUtc":
          return row.loggedOnUtc ? /* @__PURE__ */ jsxs("span", { className: "whitespace-nowrap text-[var(--muted)]", children: [
            /* @__PURE__ */ jsx("span", { className: "tabular-nums", children: parseAndFormatDate(row.loggedOnUtc, void 0, { format: DATE_FORMAT }) }),
            " ",
            /* @__PURE__ */ jsx("span", { className: "text-[var(--fg-3)]", children: parseAndFormatTime(row.loggedOnUtc, void 0, { format: TIME_FORMAT }) })
          ] }) : dash$1;
        case "customerId":
          if (row.customerId == null) return dash$1;
          return AccountLink ? /* @__PURE__ */ jsx("span", { onPointerDown: stopRowPress, onKeyDown: stopRowPress, children: /* @__PURE__ */ jsx(
            AccountLink,
            {
              accountId: Number(row.customerId),
              accountType: "customer",
              className: "font-mono text-[var(--accent)]"
            }
          ) }) : /* @__PURE__ */ jsx("span", { className: "font-mono text-[var(--muted)]", children: row.customerId });
        case "type":
          return row.type ? /* @__PURE__ */ jsx(Pill, { tone: TYPE_TONES[row.type] ?? "neutral", children: row.type }) : dash$1;
        case "direction":
          return row.direction ? /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-1 text-[var(--muted)]", children: [
            /* @__PURE__ */ jsx(
              "i",
              {
                className: row.direction === "Send" ? "ri-arrow-right-up-line text-[var(--accent)]" : "ri-arrow-left-down-line"
              }
            ),
            row.direction
          ] }) : dash$1;
        case "deviceId":
          return row.deviceId ? /* @__PURE__ */ jsx("span", { className: "font-mono text-[var(--foreground)]", children: row.deviceId }) : dash$1;
        case "phoneNumber":
          return row.phoneNumber ? /* @__PURE__ */ jsx("span", { className: "font-mono text-[var(--muted)]", children: row.phoneNumber }) : dash$1;
        case "rawValue":
          return row.rawValue ? /* @__PURE__ */ jsx(
            "span",
            {
              className: "block truncate font-mono text-[11.5px] text-[var(--muted)]",
              title: row.rawValue,
              children: row.rawValue
            }
          ) : dash$1;
        case "fromServerName":
          return row.fromServerName ? /* @__PURE__ */ jsx("span", { className: "text-[var(--fg-3)]", children: row.fromServerName }) : dash$1;
        default:
          return row[columnKey] ?? dash$1;
      }
    },
    [AccountLink]
  );
  const { filters } = useFiltersProvider();
  const hasUserFilters = filters.getAll({ scope: "all", visibility: "visible", status: "active" }).some((filter) => filter.isApplied);
  const gridColumns = useMemo(
    () => buildColumns({ showCustomer, showDevice }).map((column, index) => ({
      id: column.propertyName,
      header: column.name,
      // React Aria needs one row-header column; the device serial when shown, else the date.
      isRowHeader: showDevice ? column.propertyName === "deviceId" : index === 0,
      allowsSorting: column.isSortable,
      allowsResizing: true,
      minWidth: column.minWidth ?? 100,
      align: column.align,
      cell: (row) => renderCell(row, column.propertyName)
    })),
    [showCustomer, showDevice, renderCell]
  );
  return /* @__PURE__ */ jsx(
    RequestBrokerDataGrid,
    {
      "aria-label": "Device activity table",
      hideChips: true,
      topContent,
      pagerStyle,
      columns: gridColumns,
      data: rows,
      getRowId: (row) => row.id,
      onRowAction: onSelectRow ? (key) => onSelectRow(rows.find((row) => row.id === key)) : void 0,
      allowsColumnResize: true,
      renderEmptyState: () => hasUserFilters ? /* @__PURE__ */ jsx(EmptyState, { className: "py-10", children: /* @__PURE__ */ jsxs(EmptyState.Header, { children: [
        /* @__PURE__ */ jsx(EmptyState.Media, { variant: "icon", children: /* @__PURE__ */ jsx("i", { className: "ri-filter-off-line" }) }),
        /* @__PURE__ */ jsx(EmptyState.Title, { children: "No events match your filter." })
      ] }) }) : /* @__PURE__ */ jsx(EmptyState, { className: "py-10", children: /* @__PURE__ */ jsxs(EmptyState.Header, { children: [
        /* @__PURE__ */ jsx(EmptyState.Media, { variant: "icon", children: /* @__PURE__ */ jsx("i", { className: "ri-pulse-line" }) }),
        /* @__PURE__ */ jsx(EmptyState.Title, { children: "No device activity" }),
        /* @__PURE__ */ jsx(EmptyState.Description, { children: "Logged traffic will appear here." })
      ] }) })
    }
  );
};
const DATE_TIME_FORMAT = {
  month: "2-digit",
  day: "2-digit",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
  second: "2-digit",
  timeZoneName: "short"
};
const dash = /* @__PURE__ */ jsx("span", { className: "text-[var(--fg-3)]", children: "—" });
const isBlank = (value) => value === void 0 || value === null || value === "";
const Field = ({ label, children, mono = false }) => /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-0.5", children: [
  /* @__PURE__ */ jsx("span", { className: "text-muted text-xs", children: label }),
  /* @__PURE__ */ jsx("span", { className: `text-foreground break-words text-sm ${mono ? "font-mono" : ""}`, children: isBlank(children) ? dash : children })
] });
const dateTime = (value) => value ? parseAndFormatDateTime(value, void 0, { format: DATE_TIME_FORMAT }) : null;
const DeviceActivityDetail = withDrawerController(({ item: record }) => {
  if (!record) return null;
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-5", children: [
    /* @__PURE__ */ jsxs("div", { className: "grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2", children: [
      /* @__PURE__ */ jsx(Field, { label: "Logged On", children: dateTime(record.loggedOnUtc) }),
      /* @__PURE__ */ jsx(Field, { label: "Record Id", mono: true, children: record.id }),
      /* @__PURE__ */ jsx(Field, { label: "Type", children: record.type ? /* @__PURE__ */ jsx(Pill, { tone: TYPE_TONES[record.type] ?? "neutral", children: record.type }) : null }),
      /* @__PURE__ */ jsx(Field, { label: "Direction", children: record.direction }),
      /* @__PURE__ */ jsx(Field, { label: "Device Serial", mono: true, children: record.deviceId }),
      /* @__PURE__ */ jsx(Field, { label: "Device Type", children: record.deviceTypeName }),
      /* @__PURE__ */ jsx(Field, { label: "CSID", mono: true, children: record.csid }),
      /* @__PURE__ */ jsx(Field, { label: "Phone Number", mono: true, children: record.phoneNumber }),
      /* @__PURE__ */ jsx(Field, { label: "Customer", mono: true, children: record.customerId }),
      /* @__PURE__ */ jsx(Field, { label: "Dealer", mono: true, children: record.dealerId }),
      /* @__PURE__ */ jsx(Field, { label: "Function Name", children: record.functionName }),
      /* @__PURE__ */ jsx(Field, { label: "Server Name", children: record.fromServerName }),
      /* @__PURE__ */ jsx(Field, { label: "From IP Address", mono: true, children: record.fromIpAddress }),
      /* @__PURE__ */ jsx(Field, { label: "From User Id", mono: true, children: record.fromUserId }),
      /* @__PURE__ */ jsx(Field, { label: "Began On", children: dateTime(record.beginOnUtc) }),
      /* @__PURE__ */ jsx(Field, { label: "Ended On", children: dateTime(record.endOnUtc) }),
      /* @__PURE__ */ jsx(Field, { label: "Duplicate", children: record.isDuplicate ? "Yes" : "No" }),
      /* @__PURE__ */ jsx(Field, { label: "Duplicate Of", mono: true, children: record.duplicateId }),
      /* @__PURE__ */ jsx(Field, { label: "Message Hash", mono: true, children: record.messageHash })
    ] }),
    /* @__PURE__ */ jsx(Field, { label: "Note", children: record.note }),
    /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
        /* @__PURE__ */ jsx("span", { className: "text-muted text-xs", children: "Raw Value" }),
        !isBlank(record.rawValue) && /* @__PURE__ */ jsx(CopyToClipboard, { text: record.rawValue })
      ] }),
      /* @__PURE__ */ jsx("pre", { className: "bg-surface-secondary text-foreground max-h-96 overflow-auto whitespace-pre-wrap break-all rounded-xl p-3 font-mono text-xs", children: isBlank(record.rawValue) ? "—" : record.rawValue })
    ] })
  ] });
});
function DeviceActivityToolbar() {
  return /* @__PURE__ */ jsxs("div", { className: "flex w-full flex-wrap items-center gap-3", children: [
    /* @__PURE__ */ jsx(HorizontalFilters, { scope: "custom", classNames: { base: "flex flex-wrap items-end gap-3" } }),
    /* @__PURE__ */ jsx(HorizontalChips, { scope: "global" })
  ] });
}
const actionKeyFor = ({ customerId, deviceId }) => deviceId != null ? `deviceActivity/device/${deviceId}` : customerId != null ? `deviceActivity/customer/${customerId}` : "deviceActivity/all";
const DeviceActivityController = reactive.component(
  ({ services, data, monitors, customerId, deviceId }) => {
    const actionKey = actionKeyFor({ customerId, deviceId });
    const filters = useMemo(
      () => createDeviceActivityFilters({ customerId, deviceId }),
      [customerId, deviceId]
    );
    const sorting = useMemo(() => createDeviceActivitySorting(), []);
    const [detail, DetailDrawer] = useDrawerController(DeviceActivityDetail, {
      // Required: withDrawerController defaults isOpen to true.
      isOpen: false,
      size: "xl",
      headerContent: () => "Device Activity"
    });
    return /* @__PURE__ */ jsxs(
      RequestBroker,
      {
        actionKey,
        action: services.deviceActivity.queryDeviceActivity,
        filters,
        sorting,
        paging: new Paging(25),
        isExecuting: monitors.queryDeviceActivity.executing,
        onExecute: (action, metadata) => action(metadata, actionKey, { withCount: false }),
        children: [
          /* @__PURE__ */ jsx(
            DeviceActivityTable,
            {
              rows: data.rows ?? [],
              showCustomer: customerId == null,
              showDevice: deviceId == null,
              pagerStyle: "simple",
              onSelectRow: detail.open,
              topContent: /* @__PURE__ */ jsx(DeviceActivityToolbar, {})
            }
          ),
          /* @__PURE__ */ jsx(DetailDrawer, { classNames: { body: "px-5 py-4" } })
        ]
      },
      actionKey
    );
  },
  {
    name: "DeviceActivityController",
    queries: ({ customerId, deviceId }) => [
      {
        name: "rows",
        defaultValue: [],
        execute: (db) => db.withRequestBroker(
          actionKeyFor({ customerId, deviceId }),
          () => deviceId != null ? db.device_activity.where("deviceId").equals(String(deviceId)) : customerId != null ? db.device_activity.where("customerId").equals(Number(customerId)) : db.device_activity,
          { sorting: true }
        ),
        deps: [customerId, deviceId]
      }
    ],
    monitors: (services) => [services.deviceActivity.queryDeviceActivity]
  }
);
const DeviceActivityView = () => /* @__PURE__ */ jsxs("div", { className: "flex h-full min-h-0 w-full flex-col gap-4", children: [
  /* @__PURE__ */ jsx("div", { className: "flex justify-between items-center", children: /* @__PURE__ */ jsxs(Breadcrumbs, { children: [
    /* @__PURE__ */ jsx(BreadcrumbsItem, { isDisabled: true, children: "Devices" }),
    /* @__PURE__ */ jsx(BreadcrumbsItem, { children: "Device Activity" })
  ] }) }),
  /* @__PURE__ */ jsx(DeviceActivityController, {})
] });
const DeviceActivityRoutes = () => /* @__PURE__ */ jsx(Routes, { children: /* @__PURE__ */ jsx(Route, { path: "/*", element: /* @__PURE__ */ jsx(DeviceActivityView, {}) }) });
const Router = () => /* @__PURE__ */ jsx(AppLayout, { children: /* @__PURE__ */ jsxs(Routes, { children: [
  /* @__PURE__ */ jsx(
    Route,
    {
      path: "device-activity/*",
      element: /* @__PURE__ */ jsx(RequirePolicy, { resource: "Devices", operation: "View", placeholder: /* @__PURE__ */ jsx(Forbidden, {}), children: /* @__PURE__ */ jsx(DeviceActivityRoutes, {}) })
    }
  ),
  /* @__PURE__ */ jsx(
    Route,
    {
      path: "/*",
      element: /* @__PURE__ */ jsx(RequirePolicy, { resource: "Devices", operation: "View", placeholder: /* @__PURE__ */ jsx(Forbidden, {}), children: /* @__PURE__ */ jsx(DeviceActivityRoutes, {}) })
    }
  )
] }) });
function getDevicesNavigation(runtime) {
  const user = runtime.user;
  const apiKey = runtime.config?.api?.apiKey ?? "";
  return {
    isVisible: user.hasApiAccess(apiKey),
    items: [
      {
        title: "Device Activity",
        route: "/device-activity",
        icon: "ri-pulse-line",
        isVisible: user.hasPolicy(apiKey, "Devices", "View")
      }
    ]
  };
}
function getDevicesResources() {
  return {
    items: [],
    handlers: (() => ({}))
  };
}
const Embed = createEmbed({ runtime: () => appRuntime, render });
const TONES = {
  danger: { bar: "bg-[var(--pill-danger-fg)]", text: "text-[var(--pill-danger-fg)]" },
  warning: { bar: "bg-[var(--pill-warning-fg)]", text: "text-[var(--pill-warning-fg)]" },
  success: { bar: "bg-[var(--pill-success-fg)]", text: "text-[var(--pill-success-fg)]" }
};
const BatteryBar = ({ pct }) => {
  const tone = TONES[pct <= 20 ? "danger" : pct <= 40 ? "warning" : "success"];
  return /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
    /* @__PURE__ */ jsx("div", { className: "relative h-3.5 w-9 flex-none overflow-hidden rounded-[3px] border-[1.5px] border-[var(--border)] bg-[var(--background)]", children: /* @__PURE__ */ jsx("div", { className: `absolute inset-y-0 left-0 ${tone.bar}`, style: { width: `${pct}%` } }) }),
    /* @__PURE__ */ jsxs("span", { className: `text-[12px] font-semibold tabular-nums ${tone.text}`, children: [
      pct,
      "%"
    ] })
  ] });
};
const digitsOnly = (value) => String(value ?? "").replace(/\D/g, "");
const DeviceWidget = ({ device }) => {
  if (!device) return null;
  return /* @__PURE__ */ jsxs(Widget, { className: "w-full", children: [
    /* @__PURE__ */ jsx(Widget.Header, { children: /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center gap-3", children: [
      /* @__PURE__ */ jsxs(Pill, { tone: "info", className: "font-bold tracking-[0.04em]", children: [
        /* @__PURE__ */ jsx("i", { className: "ri-shield-check-line text-[12px]" }),
        device.type ?? "Device"
      ] }),
      /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-1.5 font-mono text-xs text-[var(--fg-3)]", children: [
        device.id,
        /* @__PURE__ */ jsx(CopyToClipboard, { text: device.id })
      ] })
    ] }) }),
    /* @__PURE__ */ jsx(Widget.Content, { children: /* @__PURE__ */ jsxs("div", { className: "grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2 md:grid-cols-4", children: [
      /* @__PURE__ */ jsx(WidgetField, { label: "Device phone", children: device.phoneNumber && /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-1.5", children: [
        formatPhone(device.phoneNumber),
        /* @__PURE__ */ jsx(CopyToClipboard, { text: digitsOnly(device.phoneNumber) })
      ] }) }),
      /* @__PURE__ */ jsx(WidgetField, { label: "SIM provider", children: device.simSupplierName }),
      /* @__PURE__ */ jsx(WidgetField, { label: "Last periodic test", children: device.lastPeriodicUtc && parseAndFormatDateTime(device.lastPeriodicUtc) }),
      /* @__PURE__ */ jsx(WidgetField, { label: "Battery", children: device.currentBatteryLevel != null && /* @__PURE__ */ jsx(BatteryBar, { pct: device.currentBatteryLevel }) }),
      device.lastEventType && /* @__PURE__ */ jsx(WidgetField, { label: "Last event", children: /* @__PURE__ */ jsxs(Pill, { tone: "danger", children: [
        /* @__PURE__ */ jsx("i", { className: "ri-pulse-line text-[12px]" }),
        device.lastEventType,
        /* @__PURE__ */ jsxs("span", { className: "text-[11.5px] font-normal", children: [
          "· ",
          parseAndFormatDateTime(device.lastEventUtc)
        ] })
      ] }) }),
      device.currentLocation && /* @__PURE__ */ jsx(WidgetField, { label: "Last known location", span: device.lastEventType ? 3 : 4, children: /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-1.5 text-[var(--accent)]", children: [
        /* @__PURE__ */ jsx("i", { className: "ri-map-pin-line text-[13px]" }),
        device.currentLocation,
        device.locationReportedOnUtc && /* @__PURE__ */ jsxs("span", { className: "text-[11.5px] text-[var(--fg-3)]", children: [
          "· ",
          parseAndFormatDateTime(device.locationReportedOnUtc)
        ] })
      ] }) })
    ] }) })
  ] });
};
const CustomerDevicesController = reactive.component(
  ({ services, monitors, customerId }) => {
    const [devices, setDevices] = useState([]);
    useSuccessReactor(services.devices.findByCustomer, (payload, args) => {
      if (Number(args[0]) === Number(customerId)) setDevices(payload ?? []);
    });
    if (devices.length === 0 && monitors.findByCustomer.executing) {
      return /* @__PURE__ */ jsxs(Widget, { className: "w-full", "aria-busy": true, children: [
        /* @__PURE__ */ jsx(Widget.Header, { children: /* @__PURE__ */ jsx(Widget.Title, { children: "Device" }) }),
        /* @__PURE__ */ jsx(Widget.Content, { children: /* @__PURE__ */ jsx(Skeleton, { className: "h-10 w-full rounded-md" }) })
      ] });
    }
    return /* @__PURE__ */ jsx(Fragment, { children: devices.map((device) => /* @__PURE__ */ jsx(DeviceWidget, { device }, device.id)) });
  },
  {
    name: "CustomerDevicesController",
    sync: (services, { customerId }) => [
      {
        execute: () => customerId && services.devices.findByCustomer(Number(customerId)),
        deps: [customerId]
      }
    ],
    monitors: (services) => [services.devices.findByCustomer]
  }
);
const CUSTOMER_DEVICES = "devices.customer-devices";
const CustomerDevices = (props) => createElement(
  Embed,
  null,
  createElement(CustomerDevicesController, props)
);
const DEVICE_TRAFFIC_TABLE = "devices.device-traffic-table";
const DeviceTrafficTable = (props) => createElement(
  Embed,
  null,
  createElement(DeviceActivityController, props)
);
let appRuntime = null;
const initDevicesRuntime = (hostRuntime) => {
  appRuntime = hostRuntime.registerApp("devices", {
    description: {
      title: "Devices",
      iconClass: "ri-device-fill",
      defaultRoute: "/device-activity",
      isVisible: (runtime) => runtime.user.hasApiAccess(runtime.config?.api?.apiKey ?? "")
    },
    routes: Router,
    navigation: getDevicesNavigation,
    resources: getDevicesResources,
    config: () => DevicesConfig.current
  });
  hostRuntime.components.register(CUSTOMER_DEVICES, CustomerDevices, {
    slot: "accounts.customer-overview",
    order: 40
  });
  hostRuntime.components.register(DEVICE_TRAFFIC_TABLE, DeviceTrafficTable);
};
export {
  initDevicesRuntime
};
//# sourceMappingURL=index.js.map
